import { useCallback, useEffect } from "react";
import { Avatar, Button, Menu } from "@mantine/core";
import { IconCaretDownFilled, IconLogout, IconPlus, IconUser } from "@tabler/icons-react";
import md5 from "md5";
import { useTranslation } from "react-i18next";
import styles from "./LoginComponent.module.scss";
import { appStore } from "@/app/store/appStore";
import { basePath } from "@/app/AppRoutes";
import OpenApiClient from "@/api/OpenApiClient";
import {
	FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType,
	FxIdApplicationHandlersAuthAuthenticateToFxIdServicesProvidersEpicGamesStoreAuthDataLoginCredentialType,
	FxIdApplicationHandlersAuthAuthenticateWithExternalIdState,
	FxIdWebFeaturesAuthAuthenticateToFxIdAdapterForExternalIdAuthenticationRequest
} from "@/api/gen";
import { emailStepStore } from "@/entities/StepEmail/store/emailStepStore";
import { menuDropdownStyles } from "./custumMantineStyles";
import { IOtherLogin, persistStore } from "@entities/StepFolder/store/persistStore";
import { stepsStore } from "@widgets/StepsManager/store/stepsStore";
import { STEPS_ENUM } from "@widgets/StepsManager/store/types";

export enum ExternalLoginStateEnum {
	notReady,
	waitingSdk,
	ready,
	authenticating,
	failed,
	success,
	emailRequestDone
}

export interface ExternalLoginState {
	type?: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType;
	UserId?: string;
	UserName?: string;
	token?: string;
	AccountType?: number;
	state: ExternalLoginStateEnum;
}

export const LoginComponent = () => {
	console.log("Render LoginComponent...");

	useEffect(() => {}, []);

	const [appCode, authToken] = appStore((state) => [state.appCode, state.authToken]);

	const { responseData, externalLoginState, login } = emailStepStore((state) => ({
		responseData: state.responseData,
		externalLoginState: state.externalLoginState,
		login: state.login
	}));

	const otherLogins = persistStore((state) => state.otherLogins);
	const isPrivacy = persistStore((state) => state.isPrivacy);

	useEffect(() => {
		persistStore.getState().loadProfile();
	}, [authToken]);

	const { logout, checkResponse, setIsLoading, startRequest, setExternalLoginState, saveCurrentLogin } =
		emailStepStore.getState();

	const { t } = useTranslation();
	const handleLogoutButtonClick = async () => {
		logout();
	};
	const handleNewLogin = async () => {
		logout();
		appStore.getState().setLoginFlow(true);
	};

	useEffect(() => {
		if (login) {
			saveCurrentLogin();
		}
	}, [login, saveCurrentLogin]);

	interface ParsedArgs {
		SteamAppId: string;
		AUTH_PASSWORD: string;
		epicuserid: string;
		epicusername: string;

		[key: string]: string | boolean; // For any additional fields
	}

	const parseArguments = useCallback((argsString: string): ParsedArgs => {
		const args: ParsedArgs = {
			AUTH_PASSWORD: "",
			SteamAppId: "",
			epicuserid: "",
			epicusername: ""
		};

		const regex = /\s-(\w+)=([^\s]+)/g;
		let match;

		while ((match = regex.exec(argsString)) !== null) {
			const [, key, value] = match;
			if (key in args) {
				args[key] = value;
			}
		}

		return args;
	}, []);

	const authenticateExternal = useCallback(async () => {
		try {
			if (!externalLoginState.type || !externalLoginState.token || !externalLoginState.UserId) {
				console.log("No external login type provided");
				return;
			}

			const request: FxIdWebFeaturesAuthAuthenticateToFxIdAdapterForExternalIdAuthenticationRequest = {
				AuthenticationType: externalLoginState.type,
				Game: appCode
			};
			if (externalLoginState.type === FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Egs) {
				request.EgsLogin = {
					Token: externalLoginState.token,
					LoginCredentialType:
						FxIdApplicationHandlersAuthAuthenticateToFxIdServicesProvidersEpicGamesStoreAuthDataLoginCredentialType.Unspecified
				};
			} else if (
				externalLoginState.type === FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Steam
			) {
				request.SteamLogin = {
					SteamSessionTicket: externalLoginState.token,
					SteamUserId: externalLoginState.UserId
				};
			} else if (
				externalLoginState.type === FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Aw
			) {
				request.AwAuthData = {
					AuthToken: externalLoginState.token,
					AccountId: parseInt(externalLoginState.UserId, 10),
					AccountType: externalLoginState.AccountType || 0,
					GameSystemName: "Aw"
				};
			}
			console.log("auth request:", request);
			const res =
				await OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateToFxIdAdapterForExternalIdEndpoint(request);
			if (res?.State === FxIdApplicationHandlersAuthAuthenticateWithExternalIdState.Success) {
				setExternalLoginState({ ...externalLoginState, state: ExternalLoginStateEnum.success });
			} else {
				setExternalLoginState({ ...externalLoginState, state: ExternalLoginStateEnum.failed });
			}
			checkResponse(res);
		} catch (error) {
			console.log(`authenticateWithSteam ${error}`);
		}
	}, [appCode, checkResponse, externalLoginState, setExternalLoginState]);

	const loginWithAW = useCallback(async () => {
		const info = await AwApi.User.GetUserInfo();

		if (info.token) {
			if (info.accountType !==3) { //UserAccountType.Gas
				const request: FxIdWebFeaturesAuthAuthenticateToFxIdAdapterForExternalIdAuthenticationRequest = {
					AuthenticationType:
						FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.AuthenticateWithExternalIdGameAccessToken,
					Game: appStore.getState().appCode,
					AuthenticateWithExternalIdGameAccessToken: {
						GameAccessToken: info.token
					}
				};
				const res =
					await OpenApiClient.Auth.fxIdWebFeaturesAuthAuthenticateToFxIdAdapterForExternalIdEndpoint(request);
				console.error(res);
				if (res.Tokens?.AccessToken) {
					emailStepStore.getState().checkResponse(res);
					await persistStore.getState().loadProfile();
				} else {
					console.error("Login failed. Please try again later");
				}
			} else {
				const backend = await AwApi.User.GetBackendUserInfo(5000);

				setExternalLoginState({
					type: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Aw,
					UserId: backend.accountId,
					AccountType: backend.accountType,
					UserName: info.name,
					token: backend.sign,
					state: ExternalLoginStateEnum.ready
				});
			}
		} else {
			console.error("no token");
		}
	}, [setExternalLoginState]);

	const authenticateMachine = useCallback(async () => {
		try {
			if (externalLoginState.state === ExternalLoginStateEnum.notReady) {
				setIsLoading(true);
				try {
					if (typeof window.AwApi !== "undefined") {
						// похоже мы внутри ав браузера, поэтому авторизуемся через AW
						await loginWithAW();
						return;
					}
					const commandLineArgs = parseArguments(FxApi.getCommandLine() || "");
					const envArgs = parseArguments(FxApi.getEnvironment().replaceAll("\n", " -"));
					console.log(`AUTH commandLineArgs:${JSON.stringify(commandLineArgs)}`);
					console.log(`AUTH envArgs:${JSON.stringify(envArgs)}`);

					if (commandLineArgs.epicuserid !== "" && commandLineArgs.AUTH_PASSWORD !== "") {
						setExternalLoginState({
							type: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Egs,
							UserId: commandLineArgs.epicuserid,
							UserName: commandLineArgs.epicusername.replaceAll('"', "") || commandLineArgs.epicuserid,
							token: commandLineArgs.AUTH_PASSWORD,
							state: ExternalLoginStateEnum.ready
						});
					} else if (envArgs.SteamAppId !== "" || commandLineArgs.SteamAppId !== "") {
						console.log("AUTH staring steaming authentication");
						setExternalLoginState({
							type: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Steam,
							state: ExternalLoginStateEnum.waitingSdk
						});
					} else {
						setExternalLoginState({
							state: ExternalLoginStateEnum.failed
						});
					}
				} catch (error) {
					console.log(`AUTH commandLineArgs ${error}`);
					setExternalLoginState({
						state: ExternalLoginStateEnum.failed
					});
				}
			} else if (externalLoginState.state === ExternalLoginStateEnum.waitingSdk) {
				try {
					if (
						externalLoginState.type ===
						FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Steam
					) {
						const steam = await FxApi.Steam.login(5000);
						console.log("AUTH steam:", steam);
						if (steam.success && steam.SteamUserId && steam.SteamSessionTicket) {
							setExternalLoginState({
								type: FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType.Steam,
								UserId: steam.SteamUserId,
								token: steam.SteamSessionTicket,
								state: ExternalLoginStateEnum.ready,
								UserName: steam.SteamName
							});
						}
					}
				} catch (error) {
					console.log(`AUTH steam error ${error}`);
					setExternalLoginState({ state: ExternalLoginStateEnum.failed });
				}
			} else if (externalLoginState.state === ExternalLoginStateEnum.ready) {
				setExternalLoginState({ ...externalLoginState, state: ExternalLoginStateEnum.authenticating });
				await authenticateExternal();
			} else if (
				externalLoginState.state === ExternalLoginStateEnum.success ||
				externalLoginState.state === ExternalLoginStateEnum.failed
			) {
				setExternalLoginState({ ...externalLoginState, state: ExternalLoginStateEnum.emailRequestDone });
				if (externalLoginState.state === ExternalLoginStateEnum.failed) {
					await startRequest();
					saveCurrentLogin();
				} else {
					emailStepStore.getState().setIsExternalLogin(true);
					emailStepStore
						.getState()
						.setLogin(
							externalLoginState.type
								? `${externalLoginState.UserName}[${FxIdApplicationHandlersAuthAuthenticateToFxIdFxIdAuthenticationType[externalLoginState.type]}]`
								: "external"
						);
				}

				setIsLoading(false);
			}
		} catch (e) {
			console.log(e);
		}
	}, [
		externalLoginState,
		setIsLoading,
		parseArguments,
		loginWithAW,
		setExternalLoginState,
		authenticateExternal,
		startRequest,
		saveCurrentLogin
	]);

	useEffect(() => {
		void authenticateMachine();
	}, [externalLoginState, authenticateMachine]);

	const gravatarUrl = login
		? `https://www.gravatar.com/avatar/${md5(login.toLowerCase())}?d=${encodeURIComponent(`${basePath}${appCode}/avatar-icon.png`)}`
		: "";

	function switchToLogin(login: IOtherLogin) {
		persistStore.getState().setRefreshToken(login.refreshToken);
		emailStepStore.getState().startRequest(login.refreshToken);
		stepsStore.getState().setActiveStep(STEPS_ENUM.EmailStep);
		appStore.getState().setLoginFlow(true);
	}

	return (
		<div className={styles.userWrapper}>
			{!login && (
				<>
					<IconUser />
					<p onClick={() => appStore.getState().setLoginFlow(true)} className={styles.userInfo}>
						{t("log_in").toUpperCase()}
					</p>
				</>
			)}
			{login && (
				<>
					<Avatar color="cyan" src={gravatarUrl} radius="xl" size="sm" />
					<p className="mantine-visible-from-md">{login.toUpperCase()}</p>
				</>
			)}

			{!externalLoginState.type && isPrivacy && (login || otherLogins.length > 0) && (
				<Menu shadow="md" width={150}>
					<Menu.Target>
						<Button p={0} radius={0} bg="transparent" ml={5} mr={5}>
							<IconCaretDownFilled className={styles.userDropdownIcon} color="white" />
						</Button>
					</Menu.Target>

					<Menu.Dropdown styles={menuDropdownStyles}>
						{login && (
							<Menu.Item
								className={styles.dropdownItem}
								onClick={handleLogoutButtonClick}
								leftSection={<IconLogout style={{ width: "14px", height: "14px" }} />}
							>
								{t("button_logout")}
							</Menu.Item>
						)}
						{login && (
							<Menu.Item
								className={styles.dropdownItem}
								onClick={handleNewLogin}
								leftSection={<IconPlus style={{ width: "14px", height: "14px" }} />}
							>
								{t("common.NewLogin", "New login")}
							</Menu.Item>
						)}
						{isPrivacy &&
							otherLogins.map(
								(login) =>
									login.appCode === appCode &&
									login.username !== responseData?.Email && (
										<Menu.Item
											key={login.username}
											className={styles.dropdownItem}
											onClick={() => switchToLogin(login)}
											leftSection={<IconUser style={{ width: "14px", height: "14px" }} />}
										>
											{login.username}
										</Menu.Item>
									)
							)}
					</Menu.Dropdown>
				</Menu>
			)}
		</div>
	);
};
